import { Component } from '@theme/component';

// El endpoint vive en el proyecto de Vercel (backend), no en linkupwear.co
// (Shopify) — por eso la URL es absoluta y no relativa. Confirmado empíricamente
// contra el alias de producción real de Vercel (curl real, no supuesto).
const PREVIEW_ENDPOINT = 'https://linkupwear-webhook.vercel.app/api/preview-design';
const PHRASE_PROPERTY_NAME = 'properties[Frase personalizada]';
const LINK_PROPERTY_NAME = 'properties[Link del QR]'; // explícitamente ignorado — no afecta el preview
const TEXT_COLOR_PROPERTY_NAME = 'properties[Color del texto]';
const PHRASE_DEBOUNCE_MS = 450;

/**
 * @typedef {object} DesignPreviewRefs
 * @property {HTMLImageElement} previewImage - La imagen que muestra el preview compuesto.
 */

/**
 * Preview en vivo del diseño (frase + QR genérico) compuesto sobre el mockup del
 * color de playera seleccionado. Escucha, vía delegación en `document`, los mismos
 * controles que YA existen en la página (el variant-picker de Color y el input de
 * la property "Frase personalizada") — no crea ni duplica esa UI.
 *
 * @extends Component<DesignPreviewRefs>
 */
export default class DesignPreviewComponent extends Component {
  /** @type {string} */
  #currentColor = '';

  /** @type {string} */
  #currentPhrase = '';

  /** @type {string} Hex del color de texto elegido ("" = el cliente no ha tocado el selector — el server usa el default fijo del garment). */
  #currentTextColor = '';

  /** @type {number | undefined} */
  #debounceTimer;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #objectUrl;

  /** @type {Record<string, string>} Mapa color -> URL de imagen base (blanco), leído del <script data-color-images> server-rendered. */
  #colorImages = {};

  #onColorChange = this.handleColorChange.bind(this);
  #onPropertyInput = this.handlePropertyInput.bind(this);
  #onTextColorChange = this.handleTextColorChange.bind(this);

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('change', this.#onColorChange);
    document.addEventListener('change', this.#onTextColorChange);
    document.addEventListener('input', this.#onPropertyInput);

    const checkedColorInput = document.querySelector('input[name^="Color-"]:checked');
    if (checkedColorInput) {
      this.#currentColor = checkedColorInput.value;
    }

    const colorImagesScript = this.querySelector('script[data-color-images]');
    if (colorImagesScript) {
      try {
        this.#colorImages = JSON.parse(colorImagesScript.textContent ?? '{}');
      } catch (error) {
        console.error('No se pudo leer el mapa de imágenes por color:', error);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#onColorChange);
    document.removeEventListener('change', this.#onTextColorChange);
    document.removeEventListener('input', this.#onPropertyInput);
    clearTimeout(this.#debounceTimer);
    this.#abortController?.abort();
    this.#revokeObjectUrl();
  }

  /**
   * El variant-picker de Color ya existe en la página (fuera de este componente) —
   * escuchamos el `change` nativo del radio/select real, que burbujea hasta
   * `document` sin nada especial. `event.target.value` es directamente el nombre
   * del color ("Black", "White", etc.), tal cual — NO el VariantSelectedEvent
   * custom del tema, cuyo payload solo trae un option-value-id, no el nombre.
   * @param {Event} event
   */
  handleColorChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
    if (!target.name || !target.name.startsWith('Color-')) return;

    this.#currentColor = target.value;
    // Cambio de color: instantáneo, sin debounce (a diferencia de la frase, que si
    // lleva debounce porque el usuario está tecleando).
    clearTimeout(this.#debounceTimer);
    this.updatePreview();
  }

  /**
   * El input de "Frase personalizada" ya existe (bloque product-custom-property) —
   * escuchamos su `input` nativo vía delegación en `document`, filtrando por el
   * `name` real que Shopify le pone. El input de "Link del QR" se ignora a
   * propósito: solo se guarda para el pedido real, nunca afecta el preview (el QR
   * del preview siempre apunta a un link genérico fijo).
   * @param {Event} event
   */
  handlePropertyInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (target.name === LINK_PROPERTY_NAME) return;
    if (target.name !== PHRASE_PROPERTY_NAME) return;

    this.#currentPhrase = target.value;
    clearTimeout(this.#debounceTimer);
    this.#debounceTimer = setTimeout(() => this.updatePreview(), PHRASE_DEBOUNCE_MS);
  }

  /**
   * El selector de "Color del texto" (blocks/product-text-color-swatches.liquid)
   * ya existe en la página — escuchamos su `change` nativo (radio group) vía
   * delegación en `document`, igual que el resto de los controles. Instantáneo,
   * sin debounce, igual que el cambio de garment (es una elección discreta, no
   * texto que se está tecleando). El server (api/preview-design.js) valida este
   * hex contra la whitelist del garment actual — nunca se confía en el valor tal
   * cual, así que mandar uno inválido (p. ej. justo después de cambiar de
   * garment, antes de que el swatch component reasigne) es inofensivo.
   * @param {Event} event
   */
  handleTextColorChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== TEXT_COLOR_PROPERTY_NAME) return;

    this.#currentTextColor = target.value;
    clearTimeout(this.#debounceTimer);
    this.updatePreview();
  }

  /**
   * Dispara la composición del preview con el estado actual (color + frase), o
   * restaura la imagen base (del color actual) si la frase está vacía. Cancela
   * cualquier request anterior todavía en vuelo para que una respuesta tardía de
   * una tecla vieja nunca pueda pisar un preview más nuevo.
   */
  async updatePreview() {
    const phrase = this.#currentPhrase.trim();

    if (!phrase) {
      this.#abortController?.abort();
      this.resetToBaseImage();
      return;
    }

    if (!this.#currentColor) return; // aún no sabemos el color — no debería pasar, el picker siempre trae uno marcado

    this.#abortController?.abort();
    const abortController = new AbortController();
    this.#abortController = abortController;

    this.classList.add('design-preview--loading');

    try {
      const response = await fetch(PREVIEW_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phrase, color: this.#currentColor, textColor: this.#currentTextColor }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error(`preview-design respondió ${response.status}`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      this.#revokeObjectUrl();
      this.#objectUrl = url;

      const { previewImage } = this.refs;
      if (previewImage) previewImage.src = url;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      // Falla silenciosa a propósito: se queda con el último preview bueno (o la
      // imagen base) en vez de romper la página por un error de red/servidor en
      // un preview cosmético — no es checkout, no es el pedido real.
      console.error('Error generando el preview de diseño:', error);
    } finally {
      // Si ya hay un abortController más nuevo que este, esta request quedó
      // obsoleta — no le toca decidir si el estado de "cargando" se apaga.
      if (this.#abortController === abortController) {
        this.classList.remove('design-preview--loading');
      }
    }
  }

  /**
   * Restaura la imagen en blanco del color actualmente seleccionado — usa el mapa
   * color -> imagen (#colorImages) en vez de un único data-base-src fijo, para que
   * cambiar de color sin haber escrito nada también actualice el mockup.
   */
  resetToBaseImage() {
    this.#revokeObjectUrl();
    const { previewImage } = this.refs;
    if (!previewImage) return;
    const colorSrc = this.#colorImages[this.#currentColor];
    previewImage.src = colorSrc || previewImage.dataset.baseSrc;
    this.classList.remove('design-preview--loading');
  }

  #revokeObjectUrl() {
    if (this.#objectUrl) {
      URL.revokeObjectURL(this.#objectUrl);
      this.#objectUrl = undefined;
    }
  }
}

if (!customElements.get('design-preview-component')) {
  customElements.define('design-preview-component', DesignPreviewComponent);
}